// frontend/src/components/MlExperiment/MlExperimentCompare.tsx

import React, { useState, useEffect } from 'react';
import { MlExperimentService } from '../../services/mlExperiment.service';
import { useAuth } from '../../contexts/AuthContext';
import './MlExperimentCompare.css';

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const pageWrapperStyle: React.CSSProperties = {
  maxWidth: '1100px',
  width: '100%',
  margin: '0 auto',
  padding: '0 20px',
  boxSizing: 'border-box',
};

export const MlExperimentCompare: React.FC = () => {
  const { selectedDataset, selectedTraceId } = useAuth();
  
  const [comparison, setComparison] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promotingId, setPromotingId] = useState<number | null>(null);
  const [productionPaths, setProductionPaths] = useState<Set<string>>(new Set());

  const loadComparison = async () => {
    if (!selectedDataset) {
      setError('Nenhum dataset selecionado. Volte para "Meus Dados" e selecione um dataset.');
      setComparison(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [data, productionModels] = await Promise.all([
        MlExperimentService.compareVersions(selectedDataset),
        MlExperimentService.listProductionModels().catch(() => []),
      ]);

      setComparison(data);

      const paths = new Set<string>(
        (productionModels || [])
          .map((m: any) => m.path)
          .filter((p: any) => typeof p === 'string' && p.trim() !== '')
      );
      setProductionPaths(paths);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao carregar comparação');
    } finally {
      setLoading(false);
    }
  };

  // Carrega automaticamente quando o componente monta (se houver dataset selecionado)
  useEffect(() => {
    if (selectedDataset) {
      loadComparison();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataset]);

  const handlePromote = async (exp: any) => {
    if (!exp.experiment_path) {
      alert('❌ Caminho do experimento indisponível para este modelo');
      return;
    }

    const confirmMsg = `Promover "${exp.model_name}" (v${exp.pipeline_version}) para produção?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setPromotingId(exp.id);
      await MlExperimentService.promote(exp.experiment_path, exp.model_name, 'production');
      alert(`✅ Modelo "${exp.model_name}" promovido para produção!`);
      await loadComparison();
    } catch (err: any) {
      alert('❌ ' + (err.response?.data?.error || err.message || 'Erro ao promover'));
    } finally {
      setPromotingId(null);
    }
  };

  // Estilos
  const cardStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #1a4a7a',
    marginBottom: '24px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '12px 16px',
    color: '#8899aa',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid #1a4a7a',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px 16px',
    color: '#e0e0e0',
    fontSize: '14px',
    borderBottom: '1px solid #16213e',
  };

  if (!selectedDataset) {
    return (
      <div style={pageWrapperStyle}>
        <div style={cardStyle}>
          <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#8899aa' }}>
            📊 Selecione um dataset na tela "Meus Dados" para comparar os treinos.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrapperStyle}>
      <div className="compare-header" style={{ marginBottom: '24px' }}>
        <h1 style={{ color: '#4fc3f7', marginBottom: '8px' }}>🤖 Comparação de Versões - ML Experiments</h1>
        <p style={{ color: '#8899aa', margin: 0 }}>
          Dataset em foco: <strong>{selectedDataset}</strong>
        </p>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#4fc3f7', margin: 0 }}>📋 Comparativo de Treinos</h3>
          <button
            onClick={loadComparison}
            disabled={loading}
            style={{
              background: '#4fc3f7',
              color: '#0a0a1a',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? '⏳ Comparando...' : '🔄 Comparar'}
          </button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239, 83, 80, 0.1)', border: '1px solid #ef5350', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#ef5350' }}>
            ❌ {error}
          </div>
        )}

        {loading && !comparison ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#8899aa' }}>⏳ Carregando comparação...</div>
        ) : comparison?.all_experiments && comparison.all_experiments.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Versão</th>
                  <th style={thStyle}>Modelo / Estratégia</th>
                  <th style={thStyle}>Métrica</th>
                  <th style={thStyle}>Decisão</th>
                  <th style={thStyle}>Trace ID</th>
                  <th style={thStyle}>Ação</th>
                </tr>
              </thead>
              <tbody>
                {comparison.all_experiments.map((exp: any, index: number) => {
                  const isProduction = productionPaths.has(exp.experiment_path);
                  return (
                    <tr key={index}>
                      <td style={tdStyle}>
                        v{renderValue(exp.pipeline_version)}
                        {isProduction && (
                          <span style={{ marginLeft: '8px', fontSize: '11px', background: 'rgba(102, 187, 106, 0.15)', color: '#66bb6a', padding: '2px 8px', borderRadius: '10px' }}>
                            🟢 Em produção
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <div>{renderValue(exp.model_name)}</div>
                        <div style={{ fontSize: '12px', color: '#8899aa' }}>{renderValue(exp.strategy_name)}</div>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontSize: '12px', color: '#8899aa' }}>{renderValue(exp.metric_name)}</div>
                        <div>{renderValue(exp.metric_value)}</div>
                      </td>
                      <td style={tdStyle}>
                        <div>{renderValue(exp.decision)}</div>
                        {exp.reason && (
                          <div style={{ fontSize: '12px', color: '#8899aa' }}>{renderValue(exp.reason)}</div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <code style={{ color: '#4fc3f7', fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {renderValue(exp.trace_id)}
                        </code>
                      </td>
                      <td style={tdStyle}>
                        {isProduction ? (
                          <span style={{ color: '#66bb6a', fontWeight: 600 }}>✅ Ativo</span>
                        ) : (
                          <button
                            onClick={() => handlePromote(exp)}
                            disabled={promotingId === exp.id || !exp.experiment_path}
                            style={{
                              background: 'transparent',
                              border: '1px solid #4fc3f7',
                              color: '#4fc3f7',
                              borderRadius: '6px',
                              padding: '4px 12px',
                              cursor: 'pointer',
                              fontSize: '13px',
                            }}
                          >
                            {promotingId === exp.id ? '⏳...' : '🚀 Promover'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#8899aa' }}>
            Nenhum experimento encontrado para o dataset <strong>{selectedDataset}</strong>.
          </div>
        )}
      </div>
    </div>
  );
};

export default MlExperimentCompare;